import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  useMoveVenueToPaywallMutation,
  useSubscriptionDataLazyQuery,
  useVenuesQuery,
} from '../../generated/graphql';
import { PaywallConfirmationDialog } from './PaywallConfirmationDialog';

export const MoveCustomerToPaywall = () => {
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [force, setForce] = useState(false);

  const [
    getSubscriptionData,
    { data: subscriptionData, loading: subscriptionLoading },
  ] = useSubscriptionDataLazyQuery();

  const {
    data: venuesData,
    loading: venuesLoading,
    error: venuesError,
  } = useVenuesQuery({
    fetchPolicy: 'cache-and-network',
  });

  const [moveCustomerToPaywall] = useMoveVenueToPaywallMutation();

  const handleMoveClick = async () => {
    if (!selectedVenueId) return;
    await getSubscriptionData({ variables: { venueId: selectedVenueId } });
    setConfirmationOpen(true);
  };

  const handleConfirm = async () => {
    moveCustomerToPaywall({
      variables: { venueId: selectedVenueId, force },
      toastConfig: {
        successText: 'Customer moved to paywall',
      },
    });
    setConfirmationOpen(false);
  };

  const handleCancel = () => {
    setConfirmationOpen(false);
  };

  const filteredVenues = useMemo(() => {
    if (!venuesData?.venues) return [];
    return [...venuesData.venues].sort((a, b) => a.name.localeCompare(b.name));
  }, [venuesData]);

  return (
    <Box width="100%" height="60%" p={3} display="flex" justifyContent="center">
      <Card sx={{ p: 2, backgroundColor: '#FAFBFF', maxWidth: '1200px' }}>
        <Box p={2}>
          <Typography variant="h3">Move User to Paywall</Typography>
        </Box>

        <Autocomplete
          fullWidth
          options={filteredVenues}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={venuesLoading}
          value={filteredVenues.find((v) => v.id === selectedVenueId) || null}
          onChange={(event, newValue) =>
            setSelectedVenueId(newValue ? newValue.id : '')
          }
          noOptionsText={
            venuesError ? 'Error loading venues' : 'No venues found'
          }
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Venue"
              variant="outlined"
              margin="normal"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {venuesLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Tooltip title="Be careful: enabling this will override the existing subscription even if it's not ended yet">
          <FormControlLabel
            control={
              <Checkbox
                checked={force}
                onChange={(e) => setForce(e.target.checked)}
              />
            }
            label="Force move"
          />
        </Tooltip>

        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 2 }}
          onClick={handleMoveClick}
          disabled={!selectedVenueId}
        >
          Move to Paywall
        </Button>

        <PaywallConfirmationDialog
          open={confirmationOpen}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          subscriptionData={subscriptionData}
          loading={subscriptionLoading}
        />
      </Card>
    </Box>
  );
};
